import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import {Typography, Collapse, Button, message, Empty, Spin, Tabs, Checkbox} from "antd";
import {DownOutlined, UpOutlined, SettingOutlined} from "@ant-design/icons";
import moment from "moment";
import orderService from "../../../../services/orderService";
import stores from "../../../../store/stores";
import DateFilter from "../../../_defaultComponents/FilterComponents/DateFilter";
import {TableWrapper} from "../../../_defaultComponents/TableWrapper/TableWrapper";
import {useFetch} from "../../../../hooks/useFetch";
import {AccountApprovalModal} from "./AccountApprovalModal";
import {formatNumberString} from "../../../../utils/helpers";
import {
    PageWrapper,
    HeaderWrapper,
    EmptyWrapper,
    AccountButtonWrapper,
    PaymentTypeContainer,
    PaymentTypeRow,
    PaymentTypeInfo,
    PaymentTypeLeft,
    PaymentTypeCenter,
    PaymentTypeRight,
    OrderDetailSection,
    OrderDetailHeader,
    OrderDetailRow,
    DateWrapper,
    ExpandedRowContent,
    TabContentWrapper
} from "./style/CourierDailyOperations.style";
import {PaymentTypesGroupingModal} from "./modals/PaymentTypesGroupingModal";
import {useTranslation} from "react-i18next";
import {CustomerOrderDetailModal} from "components/customer/customerOrdersPage/customerOrdersTable/customerOrderDetailModal/CustomerOrderDetailModal";

const {Title, Text} = Typography;
const {Panel} = Collapse;
const {TabPane} = Tabs;

export const CourierDailyOperations = observer(() => {
    const [selectedDate, setSelectedDate] = useState(moment());
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [processingReportId, setProcessingReportId] = useState(null);
    const [selectedPaymentTypes, setSelectedPaymentTypes] = useState({}); // {courierId: {paymentType: boolean}}
    const [selectedIndividualOrders, setSelectedIndividualOrders] = useState({}); // {courierId: {orderId: boolean}}
    const [expandedPaymentTypes, setExpandedPaymentTypes] = useState({}); // {courierId: {paymentType: boolean}}
    const [expandedApprovedPaymentTypes, setExpandedApprovedPaymentTypes] = useState({}); // {courierId: {paymentType: boolean}}

    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [courierToProcess, setCourierToProcess] = useState(null);
    const [openPaymentTypesGroupingModal, setOpenPaymentTypesGroupingModal] = useState(false);
    const [deliveryIdForCustomerOrderDetail, setDeliveryIdForCustomerOrderDetail] = useState("");
    const [customerOrderDetailModalVisible, setCustomerOrderDetailModalVisible] = useState(false);
    const subCompanyId = stores.meStore.selectedSubCompanyId;
    const {t} = useTranslation();

    const paymentTypeMap = {
        paid: t("paymentTypes.paid", {ns: "codPayment"}),
        creditCard: t("paymentTypes.creditCard", {ns: "codPayment"}),
        cash: t("paymentTypes.cash", {ns: "codPayment"}),
        ticketRestaurant: t("paymentTypes.ticketRestaurant", {ns: "codPayment"}),
        sodexo: t("paymentTypes.sodexo", {ns: "codPayment"}),
        multinet: t("paymentTypes.multinet", {ns: "codPayment"}),
        setCard: t("paymentTypes.setCard", {ns: "codPayment"}),
        yemekmatik: t("paymentTypes.yemekmatik", {ns: "codPayment"}),
        metropol: t("paymentTypes.metropol", {ns: "codPayment"}),
        cioCard: t("paymentTypes.cioCard", {ns: "codPayment"}),
        paye: t("paymentTypes.paye", {ns: "codPayment"}),
        winWin: t("paymentTypes.winWin", {ns: "codPayment"}),
        tokenFlex: t("paymentTypes.tokenFlex", {ns: "codPayment"}),
        unspecified: t("paymentTypes.unspecified", {ns: "codPayment"})
    };
    const [loading, data, setData, setLoading, refetch] = useFetch({
        fetchFn: async () => {
            const paymentTypesArray = Object.keys(
                stores.meStore.courierDailyOperations.paymentTypes
            ).filter(key => Boolean(stores.meStore.courierDailyOperations.paymentTypes[key]));

            const response = await orderService.getCourierEndDayReport({
                date: selectedDate.format("YYYY-MM-DD"),
                subCompanyId,
                paymentTypes: paymentTypesArray
            });

            if (response.data.success) {
                return response.data.data;
            } else {
                message.error("Veriler alınırken bir hata oluştu");
                return [];
            }
        },
        dependencies: [
            selectedDate,
            stores.meStore.courierDailyOperations.paymentTypes,
            subCompanyId
        ],
        condition: true,
        initialCall: true,
        showLoading: true,
        callbackFunc: result => {
            if (result && result.length > 0) {
                const groupedData = groupDataByCourier(result);
                setData(groupedData);
            } else {
                setData([]);
            }
            setExpandedRowKeys([]);
        },
        transform: result => {
            if (result && result.length > 0) {
                return groupDataByCourier(result);
            }
            return [];
        }
    });

    const groupDataByCourier = data => {
        const groupedMap = {};

        data.forEach(item => {
            const courierId = item?.courier?._id;

            if (!courierId || !item?.courier) {
                return;
            }

            if (!groupedMap[courierId]) {
                groupedMap[courierId] = {
                    courierId: courierId,
                    courierName: item?.courier?.fullname,
                    courierPhone: item?.courier?.cellPhone,
                    totalAmount: 0,
                    operations: []
                };
            }

            groupedMap[courierId].totalAmount += item.amount || 0;
            groupedMap[courierId].operations.push(item);
        });

        return Object.values(groupedMap);
    };

    const handleDateChange = date => {
        if (date) {
            setSelectedDate(date);
        }
    };

    const toggleRowExpansion = courierId => {
        setExpandedRowKeys(prevKeys => {
            if (prevKeys.includes(courierId)) {
                return prevKeys.filter(key => key !== courierId);
            } else {
                return [...prevKeys, courierId];
            }
        });
    };
    const getPaymentTypeName = paymentType => {
        return paymentTypeMap[paymentType] || "Belirtilmemiş";
    };

    const groupOperationsByPaymentType = (operations, status = "pending") => {
        const filtered = operations.filter(op => op.status === status);
        const grouped = {};

        filtered.forEach(operation => {
            const paymentType = operation.paymentType;
            if (!grouped[paymentType]) {
                grouped[paymentType] = {
                    paymentType,
                    paymentTypeName: getPaymentTypeName(paymentType),
                    count: 0,
                    totalAmount: 0,
                    orders: []
                };
            }
            grouped[paymentType].count += 1;
            grouped[paymentType].totalAmount += operation.amount || 0;
            grouped[paymentType].orders.push(operation);
        });

        return Object.values(grouped);
    };

    const handlePaymentTypeSelection = (courierId, paymentType, checked) => {
        setSelectedPaymentTypes(prev => ({
            ...prev,
            [courierId]: {
                ...prev[courierId],
                [paymentType]: checked
            }
        }));

        const courier = data.find(c => c.courierId === courierId);
        if (courier) {
            const paymentTypeGroup = groupOperationsByPaymentType(courier.operations).find(
                g => g.paymentType === paymentType
            );
            if (paymentTypeGroup) {
                setSelectedIndividualOrders(prev => {
                    const newState = {...prev};
                    if (!newState[courierId]) newState[courierId] = {};

                    paymentTypeGroup.orders.forEach(order => {
                        newState[courierId][order._id] = checked;
                    });

                    return newState;
                });
            }
        }
    };

    const handleIndividualOrderSelection = (courierId, orderId, checked) => {
        setSelectedIndividualOrders(prev => {
            const newState = {
                ...prev,
                [courierId]: {
                    ...prev[courierId],
                    [orderId]: checked
                }
            };

            const courier = data.find(c => c.courierId === courierId);
            if (courier) {
                const pendingPaymentTypes = groupOperationsByPaymentType(
                    courier.operations,
                    "pending"
                );
                const updatedPaymentTypes = {};

                pendingPaymentTypes.forEach(paymentGroup => {
                    const allOrdersOfThisType = paymentGroup.orders;
                    const selectedOrdersOfThisType = allOrdersOfThisType.filter(
                        order => newState[courierId]?.[order._id]
                    );

                    if (selectedOrdersOfThisType.length === allOrdersOfThisType.length) {
                        updatedPaymentTypes[paymentGroup.paymentType] = true;
                    } else {
                        updatedPaymentTypes[paymentGroup.paymentType] = false;
                    }
                });

                setSelectedPaymentTypes(prevTypes => ({
                    ...prevTypes,
                    [courierId]: {
                        ...prevTypes[courierId],
                        ...updatedPaymentTypes
                    }
                }));
            }

            return newState;
        });
    };

    const togglePaymentTypeExpansion = (courierId, paymentType) => {
        setExpandedPaymentTypes(prev => ({
            ...prev,
            [courierId]: {
                ...prev[courierId],
                [paymentType]: !prev[courierId]?.[paymentType]
            }
        }));
    };

    const toggleApprovedPaymentTypeExpansion = (courierId, paymentType) => {
        setExpandedApprovedPaymentTypes(prev => ({
            ...prev,
            [courierId]: {
                ...prev[courierId],
                [paymentType]: !prev[courierId]?.[paymentType]
            }
        }));
    };

    const handleAccountApproval = async courierId => {
        const selectedOrders = selectedIndividualOrders[courierId] || {};
        const orderIds = Object.keys(selectedOrders).filter(orderId => selectedOrders[orderId]);

        if (orderIds.length === 0) {
            message.warning("Lütfen onaylanacak siparişleri seçin");
            return;
        }
        setCourierToProcess(courierId);
        setConfirmModalVisible(true);
    };

    const [
        bulkUpdateLoading,
        bulkUpdateData,
        setBulkUpdateData,
        setBulkUpdateLoading,
        performBulkUpdate
    ] = useFetch({
        fetchFn: async () => {
            if (!courierToProcess) return null;

            const selectedOrders = selectedIndividualOrders[courierToProcess] || {};
            const orderIds = Object.keys(selectedOrders).filter(orderId => selectedOrders[orderId]);

            const result = await orderService.bulkChangeStatusEndDayReport({
                reportIds: orderIds,
                status: "approved"
            });

            return result;
        },
        dependencies: [],
        condition: false,
        initialCall: false,
        showLoading: false,
        callbackFunc: async result => {
            if (result && result.data && result.data.success) {
                setSelectedPaymentTypes(prev => ({
                    ...prev,
                    [courierToProcess]: {}
                }));
                setSelectedIndividualOrders(prev => ({
                    ...prev,
                    [courierToProcess]: {}
                }));
                const subCompanyId = stores.meStore.subcompanies[0]._id.toString();
                const paymentTypesArray = Object.keys(
                    stores.meStore.courierDailyOperations.paymentTypes
                ).filter(key => Boolean(stores.meStore.courierDailyOperations.paymentTypes[key]));

                try {
                    const response = await orderService.getCourierEndDayReport({
                        date: selectedDate.format("YYYY-MM-DD"),
                        subCompanyId: subCompanyId,
                        paymentTypes: paymentTypesArray
                    });

                    if (response.data.success) {
                        const newData = response.data.data;
                        if (newData && newData.length > 0) {
                            const groupedData = groupDataByCourier(newData);
                            setData(groupedData);
                        } else {
                            setData([]);
                        }
                        setExpandedRowKeys([]);
                        setExpandedPaymentTypes({});
                        setExpandedApprovedPaymentTypes({});
                    }
                } catch (error) {
                    console.error(result?.data?.message || error?.message);
                }

                message.success(result?.data?.message);
            } else {
                message.error(result?.data?.message);
            }
            setProcessingReportId(null);
            setCourierToProcess(null);
        }
    });

    const handleConfirmAccountApproval = async () => {
        if (courierToProcess) {
            setProcessingReportId(courierToProcess);
            setConfirmModalVisible(false);

            try {
                await performBulkUpdate();
            } catch (error) {
                setProcessingReportId(null);
                setCourierToProcess(null);
            }
        }
    };

    const handleCancelAccountApproval = () => {
        setConfirmModalVisible(false);
        setCourierToProcess(null);
    };

    const getSelectedOrdersCount = courierId => {
        const selectedOrders = selectedIndividualOrders[courierId] || {};
        return Object.values(selectedOrders).filter(Boolean).length;
    };

    const handleTabChange = (activeKey, courierId) => {
        setExpandedPaymentTypes(prev => ({
            ...prev,
            [courierId]: {}
        }));
        setExpandedApprovedPaymentTypes(prev => ({
            ...prev,
            [courierId]: {}
        }));
    };

    const columns = [
        {
            title: "Kurye Bilgileri",
            dataIndex: "courierName",
            key: "courierName",
            render: (text, record) => (
                <div>
                    <div>{record.courierName}</div>
                    <Text type="secondary">{record.courierPhone}</Text>
                </div>
            )
        },
        {
            title: "Toplam Tutar",
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: text => <span>{formatNumberString(text)} ₺</span>
        },
        {
            title: "İşlemler",
            key: "actions",
            render: (_, record) => (
                <Button
                    type="text"
                    onClick={() => toggleRowExpansion(record.courierId)}
                    icon={
                        expandedRowKeys.includes(record.courierId) ? (
                            <UpOutlined />
                        ) : (
                            <DownOutlined />
                        )
                    }
                >
                    {expandedRowKeys.includes(record.courierId) ? "Gizle" : "Detayları Göster"}
                </Button>
            )
        }
    ];

    const handleGroupPaymentTypes = () => {
        setOpenPaymentTypesGroupingModal(true);
    };

    const expandedRowRender = record => {
        const pendingPaymentTypes = groupOperationsByPaymentType(record.operations, "pending");
        const approvedPaymentTypes = groupOperationsByPaymentType(record.operations, "approved");
        const selectedOrdersCount = getSelectedOrdersCount(record.courierId);

        return (
            <ExpandedRowContent>
                <Tabs
                    defaultActiveKey="pending"
                    onChange={activeKey => handleTabChange(activeKey, record.courierId)}
                >
                    <TabPane tab="Hesap Beklenenler" key="pending">
                        <TabContentWrapper>
                            {pendingPaymentTypes.length > 0 ? (
                                <>
                                    {pendingPaymentTypes.map(paymentGroup => {
                                        const isExpanded =
                                            expandedPaymentTypes[record.courierId]?.[
                                                paymentGroup.paymentType
                                            ];
                                        const isSelected =
                                            selectedPaymentTypes[record.courierId]?.[
                                                paymentGroup.paymentType
                                            ];

                                        return (
                                            <PaymentTypeContainer key={paymentGroup.paymentType}>
                                                <PaymentTypeRow
                                                    onClick={() =>
                                                        togglePaymentTypeExpansion(
                                                            record.courierId,
                                                            paymentGroup.paymentType
                                                        )
                                                    }
                                                >
                                                    <PaymentTypeInfo>
                                                        <PaymentTypeLeft>
                                                            <div
                                                                onClick={e => e.stopPropagation()}
                                                                onKeyDown={e => e.stopPropagation()}
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    onChange={e =>
                                                                        handlePaymentTypeSelection(
                                                                            record.courierId,
                                                                            paymentGroup.paymentType,
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                            <div>
                                                                <Text strong>
                                                                    {paymentGroup.paymentTypeName}
                                                                </Text>
                                                            </div>
                                                        </PaymentTypeLeft>
                                                        <PaymentTypeCenter>
                                                            <Text>{paymentGroup.count} Adet</Text>
                                                        </PaymentTypeCenter>
                                                        <PaymentTypeRight>
                                                            <Text strong>
                                                                {formatNumberString(
                                                                    paymentGroup.totalAmount
                                                                )}{" "}
                                                                ₺
                                                            </Text>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                tabIndex={-1}
                                                                icon={
                                                                    isExpanded ? (
                                                                        <UpOutlined />
                                                                    ) : (
                                                                        <DownOutlined />
                                                                    )
                                                                }
                                                            />
                                                        </PaymentTypeRight>
                                                    </PaymentTypeInfo>
                                                </PaymentTypeRow>

                                                {isExpanded && (
                                                    <OrderDetailSection>
                                                        <OrderDetailHeader>
                                                            <Text strong>Sipariş Numarası</Text>
                                                            <Text strong>Tutar</Text>
                                                        </OrderDetailHeader>
                                                        {paymentGroup.orders.map(order => (
                                                            <OrderDetailRow key={order._id}>
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "12px"
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={
                                                                            selectedIndividualOrders[
                                                                                record.courierId
                                                                            ]?.[order._id] || false
                                                                        }
                                                                        onChange={e =>
                                                                            handleIndividualOrderSelection(
                                                                                record.courierId,
                                                                                order._id,
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                    />
                                                                    <Text
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            textDecoration:
                                                                                "underline",
                                                                            color: "#1890ff"
                                                                        }}
                                                                        onClick={() => {
                                                                            if (order.deliveryId) {
                                                                                setDeliveryIdForCustomerOrderDetail(
                                                                                    order.deliveryId
                                                                                );
                                                                                setCustomerOrderDetailModalVisible(
                                                                                    true
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        {order.orderNumber}
                                                                    </Text>
                                                                </div>
                                                                <Text strong>
                                                                    {formatNumberString(
                                                                        order.amount
                                                                    )}{" "}
                                                                    ₺
                                                                </Text>
                                                            </OrderDetailRow>
                                                        ))}
                                                    </OrderDetailSection>
                                                )}
                                            </PaymentTypeContainer>
                                        );
                                    })}

                                    {
                                        <AccountButtonWrapper>
                                            <Button
                                                disabled={selectedOrdersCount === 0}
                                                style={{
                                                    backgroundColor:
                                                        selectedOrdersCount === 0
                                                            ? "#ccc"
                                                            : "#AEF001",
                                                    color: "#000"
                                                }}
                                                size="large"
                                                onClick={() =>
                                                    handleAccountApproval(record.courierId)
                                                }
                                                loading={processingReportId === record.courierId}
                                            >
                                                Hesap Al ({selectedOrdersCount})
                                            </Button>
                                        </AccountButtonWrapper>
                                    }
                                </>
                            ) : (
                                <Empty description={t("noPendingOrders", {ns: "codPayment"})} />
                            )}
                        </TabContentWrapper>
                    </TabPane>

                    <TabPane tab="Hesap Alınanlar" key="approved">
                        <TabContentWrapper>
                            {approvedPaymentTypes.length > 0 ? (
                                approvedPaymentTypes.map(paymentGroup => {
                                    const isApprovedExpanded =
                                        expandedApprovedPaymentTypes[record.courierId]?.[
                                            paymentGroup.paymentType
                                        ];

                                    return (
                                        <PaymentTypeContainer key={paymentGroup.paymentType}>
                                            <PaymentTypeRow
                                                style={{backgroundColor: "#f6ffed"}}
                                                onClick={() =>
                                                    toggleApprovedPaymentTypeExpansion(
                                                        record.courierId,
                                                        paymentGroup.paymentType
                                                    )
                                                }
                                            >
                                                <PaymentTypeInfo>
                                                    <PaymentTypeLeft>
                                                        <div>
                                                            <Text strong>
                                                                {paymentGroup.paymentTypeName}
                                                            </Text>
                                                        </div>
                                                    </PaymentTypeLeft>
                                                    <PaymentTypeCenter>
                                                        <Text>{paymentGroup.count} Adet</Text>
                                                    </PaymentTypeCenter>
                                                    <PaymentTypeRight>
                                                        <Text strong style={{color: "#52c41a"}}>
                                                            {formatNumberString(
                                                                paymentGroup.totalAmount
                                                            )}{" "}
                                                            ₺
                                                        </Text>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            tabIndex={-1}
                                                            icon={
                                                                isApprovedExpanded ? (
                                                                    <UpOutlined />
                                                                ) : (
                                                                    <DownOutlined />
                                                                )
                                                            }
                                                        />
                                                    </PaymentTypeRight>
                                                </PaymentTypeInfo>
                                            </PaymentTypeRow>

                                            {isApprovedExpanded && (
                                                <OrderDetailSection>
                                                    <OrderDetailHeader>
                                                        <Text strong>Sipariş Numarası</Text>
                                                        <Text strong>Tutar</Text>
                                                    </OrderDetailHeader>
                                                    {paymentGroup.orders.map(order => (
                                                        <OrderDetailRow key={order._id}>
                                                            <div>
                                                                <Text
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                        color: "#1890ff"
                                                                    }}
                                                                    onClick={() => {
                                                                        if (order.deliveryId) {
                                                                            setDeliveryIdForCustomerOrderDetail(
                                                                                order.deliveryId
                                                                            );
                                                                            setCustomerOrderDetailModalVisible(
                                                                                true
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    {order.orderNumber}
                                                                </Text>
                                                            </div>
                                                            <Text strong style={{color: "#52c41a"}}>
                                                                {formatNumberString(order.amount)} ₺
                                                            </Text>
                                                        </OrderDetailRow>
                                                    ))}
                                                </OrderDetailSection>
                                            )}
                                        </PaymentTypeContainer>
                                    );
                                })
                            ) : (
                                <Empty description={t("noApprovedOrders", {ns: "codPayment"})} />
                            )}
                        </TabContentWrapper>
                    </TabPane>
                </Tabs>
            </ExpandedRowContent>
        );
    };

    return (
        <PageWrapper>
            <HeaderWrapper>
                <DateWrapper>
                    <h5>Tarih</h5>
                    <DateFilter
                        showLabel={false}
                        value={selectedDate}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        allowClear={false}
                    />
                </DateWrapper>
                <Button
                    style={{backgroundColor: "#AEF001", color: "#000"}}
                    onClick={handleGroupPaymentTypes}
                    icon={<SettingOutlined />}
                >
                    Ödeme Yöntemlerini Filtrele
                </Button>
                <PaymentTypesGroupingModal
                    openPaymentTypesGroupingModal={openPaymentTypesGroupingModal}
                    setOpenPaymentTypesGroupingModal={setOpenPaymentTypesGroupingModal}
                    paymentTypeMap={paymentTypeMap}
                    setPaymentTypes={stores.meStore.setCourierDailyOperationsPaymentTypes}
                    currentPaymentTypes={stores.meStore.courierDailyOperations.paymentTypes}
                />
            </HeaderWrapper>

            <Spin spinning={loading} style={{display: "flex", justifyContent: "center"}}>
                {data.length > 0 ? (
                    <div style={{marginTop: "20px"}}>
                        <TableWrapper
                            columns={columns}
                            dataSource={data}
                            rowKey="courierId"
                            expandable={{
                                expandedRowRender,
                                expandedRowKeys,
                                onExpand: (expanded, record) => toggleRowExpansion(record.courierId)
                            }}
                            pagination={false}
                        />
                        <AccountApprovalModal
                            visible={confirmModalVisible}
                            onConfirm={handleConfirmAccountApproval}
                            onCancel={handleCancelAccountApproval}
                            selectedOrdersCount={
                                courierToProcess ? getSelectedOrdersCount(courierToProcess) : 0
                            }
                            loading={processingReportId === courierToProcess}
                        />
                    </div>
                ) : (
                    <EmptyWrapper>
                        <Empty description="Seçilen tarihte kurye işlemi bulunamadı" />
                    </EmptyWrapper>
                )}
            </Spin>
            <CustomerOrderDetailModal
                visible={customerOrderDetailModalVisible}
                onCancel={() => {
                    setCustomerOrderDetailModalVisible(false);
                }}
                deliveryId={deliveryIdForCustomerOrderDetail}
            />
        </PageWrapper>
    );
});
